import {
  Component,
  ErrorHandler,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation
} from '@angular/core';
import { UserModel } from '@app/core/model/user.model';
import { ThemeService } from '@app/core/service/theme.service';
import { Capacitor } from '@capacitor/core';
import { Subject, takeUntil } from 'rxjs';
import { SurveyModule } from 'survey-angular-ui';
import { Model, Serializer } from 'survey-core';
import { PlainDarkPanelless, PlainLightPanelless } from 'survey-core/themes';

@Component({
  selector: 'app-surveyjs-viewer',
  templateUrl: './surveyjs-viewer.component.html',
  styleUrls: [
    '../../../../../node_modules/survey-core/survey-core.min.css',
    './surveyjs-viewer.component.scss'
  ],
  imports: [SurveyModule],
  encapsulation: ViewEncapsulation.None,
  standalone: true
})
export class SurveyJsViewerComponent implements OnInit, OnDestroy {
  @Input()
  public readonly value?: string = '';

  @Input()
  public readonly user?: UserModel;

  @Input()
  public readonly isAnonymous?: boolean;

  @Input()
  public readonly showCompleteButton: boolean = false;

  @Input()
  public completeHandler?: (payload: string) => Promise<unknown> | void;

  @Output()
  public complete = new EventEmitter<string>();

  protected surveyModel: Model;
  private readonly _destroy$ = new Subject<void>();
  private _isCompletingInternally = false;
  private _pendingCompletion: PromiseLike<unknown> | null = null;

  public constructor(
    private readonly _themeService: ThemeService,
    private readonly _errorHandler: ErrorHandler
  ) {}

  public ngOnInit() {
    const viewer = new Model(this.value);
    this.surveyModel = viewer;

    if (
      Capacitor.getPlatform() === 'ios' ||
      (Capacitor.getPlatform() === 'web' &&
        /iPad|iPhone|iPod/.test(navigator.userAgent))
    ) {
      Serializer.getProperty('dropdown', 'renderAs').defaultValue = 'select';
    }

    if (this.user && !this.isAnonymous) {
      const fullName = `${this.user.firstName} ${this.user.lastName}`.trim();
      this.surveyModel.setValue('username', fullName);
      this.surveyModel
        .getQuestionByName('username')
        ?.setPropertyValue('readOnly', true);
    }

    this.surveyModel.showCompleteButton = this.showCompleteButton;

    this.surveyModel.onCompleting.add((sender, options) => {
      this._handleCompleting(sender, options);
    });

    this._applyTheme(this._themeService.activeTheme);

    this._themeService.activeTheme$
      .pipe(takeUntil(this._destroy$))
      .subscribe((theme) => {
        this._applyTheme(theme);
      });
  }

  private _applyTheme(theme: 'light' | 'dark'): void {
    const baseTheme =
      theme === 'dark' ? PlainDarkPanelless : PlainLightPanelless;

    this.surveyModel.applyTheme({
      ...baseTheme,
      cssVariables: {
        ...baseTheme.cssVariables,
        '--sjs-primary-backcolor': 'var(--ion-color-primary)',
        '--sjs-primary-backcolor-dark': 'var(--ion-color-primary)',
        '--sjs-primary-backcolor-light': 'var(--ion-color-primary-light)',
        '--sjs-special-background':
          theme === 'dark' ? 'var(--engine-page-background)' : '#ffffff',
        '--sjs-primary-background-500': 'var(--ion-color-primary)',
        '--sjs-secondary-background-500': 'var(--ion-color-primary)'
      }
    });
  }

  public tryComplete() {
    this.surveyModel.tryComplete();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _handleCompleting(
    sender: Model,
    options: { allowComplete: boolean }
  ) {
    if (this._consumeInternalCompletionFlag()) return;

    if (this._pendingCompletion) {
      return;
    }

    const payload = JSON.stringify(sender.data);

    let result: unknown;
    try {
      result = this._notifyCompletion(payload);
    } catch (err) {
      this._errorHandler.handleError(err);
      return;
    }

    const completion = Promise.resolve(result);

    options.allowComplete = false;
    this._pendingCompletion = completion;

    completion
      .catch((err) => {
        this._errorHandler.handleError(err);
      })
      .finally(() => {
        if (this._pendingCompletion !== completion) return;

        this._pendingCompletion = null;
        this._isCompletingInternally = true;
        sender.doComplete();
      });
  }

  private _consumeInternalCompletionFlag(): boolean {
    if (!this._isCompletingInternally) return false;
    this._isCompletingInternally = false;
    return true;
  }

  private _notifyCompletion(payload: string): unknown {
    if (this.completeHandler) {
      return this.completeHandler(payload);
    }
    this.complete.emit(payload);
  }
}
